'use client';

import { useEffect, useRef } from 'react';
import { Box } from '@terra/ui-styled-system';

type ScrollLoadTriggerProps = {
  isEnabled: boolean;
  onLoadMore: () => void;
  resetKey?: string | number;
  dataTestid?: string;
  rootMargin?: string;
};

const resolveScrollRoot = (element: HTMLElement): Element | null => {
  let node: HTMLElement | null = element.parentElement;

  while (node) {
    const { overflowY } = getComputedStyle(node);

    if (
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflowY === 'overlay'
    ) {
      return node;
    }

    node = node.parentElement;
  }

  return null;
};

export function ScrollLoadTrigger({
  isEnabled,
  onLoadMore,
  resetKey,
  dataTestid = 'scroll-load-trigger',
  rootMargin = '120px',
}: ScrollLoadTriggerProps) {
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const onLoadMoreRef = useRef(onLoadMore);

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    const triggerElement = triggerRef.current;

    if (!triggerElement || !isEnabled) {
      return;
    }

    const scrollRoot = resolveScrollRoot(triggerElement);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          onLoadMoreRef.current();
        }
      },
      {
        root: scrollRoot,
        rootMargin,
      },
    );

    observer.observe(triggerElement);

    return () => {
      observer.disconnect();
    };
  }, [isEnabled, resetKey, rootMargin]);

  return <Box ref={triggerRef} h={0.5} data-testid={dataTestid} />;
}
